/**
 * Extrae conexiones PON OLT → fibra de cable desde el config de nodos POP/OLT.
 * @module utils/oltCableBindings
 */

export interface OltFibraBinding {
  nodeId: string
  nodeNombre: string
  oltId: string
  slotNumero: number
  puertoNumero: number
  cableId: string
  fibraId: string
}

function walkSlots(
  slots: unknown,
  nodeId: string,
  nodeNombre: string,
  oltId: string,
  out: OltFibraBinding[]
) {
  if (!Array.isArray(slots)) return
  for (const slot of slots as Array<{ numero?: number; puertos?: unknown }>) {
    const slotNumero = typeof slot?.numero === 'number' ? slot.numero : 0
    const puertos = slot?.puertos
    if (!Array.isArray(puertos)) continue
    for (const puerto of puertos as Array<{ numero?: number; conexionCable?: { cableId?: string; fibraId?: string } }>) {
      const cc = puerto?.conexionCable
      const cableId = cc?.cableId
      const fibraId = cc?.fibraId
      if (!cableId || !fibraId) continue
      out.push({
        nodeId,
        nodeNombre,
        oltId,
        slotNumero,
        puertoNumero: typeof puerto?.numero === 'number' ? puerto.numero : 0,
        cableId,
        fibraId,
      })
    }
  }
}

/**
 * Recorre un objeto config (POP/OLT) y devuelve todos los pares cable+fibra conectados a PON.
 */
export function collectOltCableFibraBindingsFromConfig(
  config: unknown,
  nodeId: string,
  nodeNombre: string
): OltFibraBinding[] {
  const out: OltFibraBinding[] = []
  if (!config || typeof config !== 'object') return out
  const c = config as Record<string, unknown>

  if (Array.isArray(c.olts) && c.olts.length > 0) {
    for (const olt of c.olts as Array<{ id?: string; slots?: unknown }>) {
      const oltId = typeof olt?.id === 'string' ? olt.id : 'olt'
      walkSlots(olt?.slots, nodeId, nodeNombre, oltId, out)
    }
    return out
  }

  if (Array.isArray(c.slots) && c.slots.length > 0) {
    walkSlots(c.slots, nodeId, nodeNombre, 'legacy', out)
  }

  return out
}

export function extractOltCableBindings(
  nodes: ReadonlyArray<{ id: string; tipo: string; nombre?: string; config?: unknown }>
): OltFibraBinding[] {
  const all: OltFibraBinding[] = []
  for (const n of nodes) {
    const t = (n.tipo || '').toUpperCase()
    if (t !== 'POP' && t !== 'OLT') continue
    const cfg = n.config
    if (cfg == null) continue
    const nombre = n.nombre || n.id.slice(0, 8)
    all.push(...collectOltCableFibraBindingsFromConfig(cfg, n.id, nombre))
  }
  return all
}

/** Bindings de OLT para un cable concreto, indexados por fibraId */
export function getOltBindingsForCable(
  cableId: string,
  bindings: OltFibraBinding[]
): Map<string, OltFibraBinding> {
  const map = new Map<string, OltFibraBinding>()
  for (const b of bindings) {
    if (b.cableId === cableId) map.set(b.fibraId, b)
  }
  return map
}

export function oltConectadaARef(binding: OltFibraBinding): string {
  return `NODE:${binding.nodeId}|OLT:${binding.oltId}|S${binding.slotNumero}|P${binding.puertoNumero}`
}

export function isOltConectadaAForNode(conectadaA: string | null | undefined, nodeId: string): boolean {
  if (!conectadaA || typeof conectadaA !== 'string') return false
  return conectadaA.startsWith(`NODE:${nodeId}|`)
}
